import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useBlockingDownload } from "../hooks/useBlockingDownload";
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getExpenses, getTeamExpenses, submitExpense, approveExpense, rejectExpense, markExpensePaid, deleteExpense, downloadReceipt, getTenantCurrency, getErrorMessage } from "../api";
import { requiredTrimmed, positiveAmount, requiredValue, collectErrors } from "../utils/validate";
import { useAuth } from "../context/AuthContext";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";

const NAVY = "#0f172a";

const errorTextStyle: React.CSSProperties = { color: "#dc2626", fontSize: 12, marginTop: 4 };

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  pending:  { label: "Pending",  bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  approved: { label: "Approved", bg: "#dcfce7", text: "#166534", border: "#86efac" },
  rejected: { label: "Rejected", bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  paid:     { label: "Paid",     bg: "#eff6ff", text: "#1d4ed8", border: "#93c5fd" },
};

const CATEGORIES = ["travel", "meals", "accommodation", "equipment", "training", "other"];

const WHT_RATE_OPTIONS = [
  { label: "Professional Services", rate: "3" },
  { label: "Advertising", rate: "2" },
  { label: "Rent", rate: "5" },
  { label: "Transport", rate: "1" },
];

export default function ExpensesPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { downloading, download } = useBlockingDownload();

  const [tab, setTab] = useState<"mine" | "team">("mine");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject">("approve");
  const [reviewNotes, setReviewNotes] = useState("");
  const [form, setForm] = useState({
    title: "", amount: "", currency: "USD", currency_symbol: "$",
    category: "other", description: "", expense_date: "",
    is_vat_claimable: false, wht_rate: "",
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: myExpenses = [] } = useQuery({
    queryKey: ["expenses", statusFilter],
    queryFn: () => getExpenses(statusFilter ? { status: statusFilter } : undefined),
  });

  const { data: teamExpenses = [] } = useQuery({
    queryKey: ["expenses-team", statusFilter],
    queryFn: () => getTeamExpenses(statusFilter ? { status: statusFilter } : undefined),
  });

  const canSeeTeam = user?.role !== "employee" && user?.role !== "recruiter";
  const hasTeam = canSeeTeam; // always show team tab for managers/admins, even when empty
  const isAdmin = ["admin", "admin_assistant", "hr", "finance"].includes(user?.role || "");
  const expenses = tab === "team" ? teamExpenses : myExpenses;
  const isTeamView = tab === "team";

  const { data: currencyData } = useQuery({ queryKey: ["tenant-currency"], queryFn: getTenantCurrency });
  React.useEffect(() => {
    if (currencyData) setForm(f => ({ ...f, currency: (currencyData as any).currency || "USD", currency_symbol: (currencyData as any).currency_symbol || "$" }));
  }, [currencyData]);

  const submitMut = useMutation({
    mutationFn: submitExpense,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["expenses"] }); setShowForm(false); resetForm(); },
    onError: (err: any) => setSubmitError(getErrorMessage(err, "Could not submit the claim — please try again.")),
  });

  const invalidateAll = () => { qc.invalidateQueries({ queryKey: ["expenses"] }); qc.invalidateQueries({ queryKey: ["expenses-team"] }); };

  const approveMut = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) => approveExpense(id, { notes }),
    onSuccess: () => { invalidateAll(); setReviewId(null); setReviewNotes(""); },
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) => rejectExpense(id, { notes }),
    onSuccess: () => { invalidateAll(); setReviewId(null); setReviewNotes(""); },
  });

  const paidMut = useMutation({
    mutationFn: markExpensePaid,
    onSuccess: () => invalidateAll(),
  });

  const deleteMut = useMutation({
    mutationFn: deleteExpense,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["expenses"] }),
  });

  function resetForm() {
    setForm(f => ({ ...f, title: "", amount: "", category: "other", description: "", expense_date: "", is_vat_claimable: false, wht_rate: "" }));
    setReceiptFile(null);
    setFieldErrors({});
    setSubmitError(null);
  }

  function setField(field: string, value: string | boolean) {
    setForm(f => ({ ...f, [field]: value }));
    setFieldErrors(e => { const { [field]: _, ...rest } = e; return rest; });
    setSubmitError(null);
  }

  async function handleSubmit() {
    const errors = collectErrors({
      title: requiredTrimmed(form.title, "Title"),
      amount: positiveAmount(form.amount, "Amount"),
      expense_date: requiredValue(form.expense_date, "Expense date"),
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    const fd = new FormData();
    Object.entries({ ...form, title: form.title.trim() }).forEach(([k, v]) => fd.append(k, v as string));
    if (receiptFile) fd.append("receipt", receiptFile);
    submitMut.mutate(fd);
  }

  async function handleDownloadReceipt(id: number) {
    await download(() => downloadReceipt(id), "Failed to download receipt");
  }

  const totalPending  = (expenses as any[]).filter((e: any) => e.status === "pending").reduce((s: number, e: any) => s + e.amount, 0);
  const totalApproved = (expenses as any[]).filter((e: any) => e.status === "approved").reduce((s: number, e: any) => s + e.amount, 0);

  return (
    <div>
      <LoadingOverlay active={
        submitMut.isPending || approveMut.isPending || rejectMut.isPending || paidMut.isPending ||
        deleteMut.isPending || downloading
      } />
      <div className="page-header">
        <div>
          <h1>Reimbursements</h1>
          <p>Submit and manage out-of-pocket expense claims</p>
        </div>
        {tab === "mine" && (
          <button onClick={() => setShowForm(f => !f)} style={primaryBtnStyle}>
            {showForm ? "Cancel" : "+ New Claim"}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="tab-pill-bar" style={{ marginBottom: 20 }}>
        {(["mine", ...(hasTeam ? ["team"] : [])] as ("mine" | "team")[]).map(t => (
          <button key={t} onClick={() => { setTab(t); setShowForm(false); }} className={tab === t ? "active" : ""}>
            {t === "mine" ? "My Claims" : `${isAdmin ? "All Claims" : "My Team"}${(teamExpenses as any[]).filter((e:any)=>e.status==="pending").length > 0 ? ` (${(teamExpenses as any[]).filter((e:any)=>e.status==="pending").length} pending)` : ""}`}
          </button>
        ))}
      </div>

      {/* Summary chips */}
      {(expenses as any[]).length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          {[
            { label: "Pending", value: totalPending, color: "#d97706", bg: "#fef9c3" },
            { label: "Approved (unpaid)", value: totalApproved, color: "#16a34a", bg: "#dcfce7" },
          ].map(({ label, value, color, bg }) => (
            <div key={label} style={{ background: bg, border: `1px solid ${color}30`, borderRadius: 10, padding: "10px 18px" }}>
              <div style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color }}>{form.currency_symbol}{value.toLocaleString("en", { minimumFractionDigits: 2 })}</div>
            </div>
          ))}
        </div>
      )}

      {/* New expense form */}
      {showForm && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 16px", color: NAVY, fontSize: 16 }}>New Reimbursement Claim</h3>
          <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Title *</label>
              <input value={form.title} onChange={e => setField("title", e.target.value)}
                placeholder="e.g. Client dinner, Flight to Bangkok" style={inputStyle} />
              {fieldErrors.title && <div style={errorTextStyle}>{fieldErrors.title}</div>}
            </div>
            <div>
              <label style={labelStyle}>Amount *</label>
              <div style={{ display: "flex", gap: 0 }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  padding: "0 10px", minWidth: 36,
                  background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRight: "none",
                  borderRadius: "8px 0 0 8px", fontSize: 14, fontWeight: 700, color: "#475569",
                }}>{form.currency_symbol}</span>
                <input type="number" value={form.amount} onChange={e => setField("amount", e.target.value)}
                  placeholder="0.00" style={{ ...inputStyle, borderRadius: "0 8px 8px 0", flex: 1 }} />
              </div>
              {fieldErrors.amount && <div style={errorTextStyle}>{fieldErrors.amount}</div>}
            </div>
            <div>
              <label style={labelStyle}>Expense Date *</label>
              <DateInput value={form.expense_date} onChange={e => setField("expense_date", e.target.value)}
                style={inputStyle} />
              {fieldErrors.expense_date && <div style={errorTextStyle}>{fieldErrors.expense_date}</div>}
            </div>
            <div>
              <label style={labelStyle}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Receipt (optional)</label>
              <input type="file" accept="image/*,.pdf" onChange={e => setReceiptFile(e.target.files?.[0] || null)}
                style={{ fontSize: 13 }} />
            </div>
          </div>
          <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#475569", fontWeight: 600 }}>
              <input type="checkbox" checked={form.is_vat_claimable}
                onChange={e => setForm(f => ({ ...f, is_vat_claimable: e.target.checked, wht_rate: e.target.checked ? f.wht_rate : "" }))} />
              VAT Claimable
            </label>
            {form.is_vat_claimable && (
              <select value={form.wht_rate} onChange={e => setForm(f => ({ ...f, wht_rate: e.target.value }))} style={{ ...inputStyle, maxWidth: 260 }}>
                <option value="">WHT rate (optional)</option>
                {WHT_RATE_OPTIONS.map(o => <option key={o.label} value={o.rate}>{o.label} ({o.rate}%)</option>)}
              </select>
            )}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Additional details…" rows={2}
              style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          {submitError && <div style={{ ...errorTextStyle, marginBottom: 10 }}>{submitError}</div>}
          <button onClick={handleSubmit} disabled={submitMut.isPending} style={primaryBtnStyle}>
            {submitMut.isPending ? "Submitting…" : "Submit Claim"}
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className="filter-bar" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["", "pending", "approved", "rejected", "paid"].map(s => (
          <button key={s || "all"} onClick={() => setStatusFilter(s)} style={{
            padding: "6px 14px", borderRadius: 20, border: "1px solid #e2e8f0", cursor: "pointer",
            fontSize: 13, fontWeight: 500,
            background: statusFilter === s ? NAVY : "white",
            color: statusFilter === s ? "white" : "#64748b",
          }}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}</button>
        ))}
      </div>

      {/* Review modal */}
      {reviewId !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div className="modal-sheet" style={{ background: "white", borderRadius: 12, padding: 24 }}>
            <h3 style={{ margin: "0 0 14px", color: NAVY }}>
              {reviewAction === "approve" ? "Approve" : "Reject"} Expense Claim
            </h3>
            <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
              placeholder="Notes (optional)" rows={3}
              style={{ ...inputStyle, marginBottom: 14 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { setReviewId(null); setReviewNotes(""); }} style={outlineBtnStyle}>Cancel</button>
              <button
                disabled={approveMut.isPending || rejectMut.isPending}
                onClick={() => {
                  if (reviewAction === "approve") approveMut.mutate({ id: reviewId!, notes: reviewNotes });
                  else rejectMut.mutate({ id: reviewId!, notes: reviewNotes });
                }}
                style={{
                  ...primaryBtnStyle,
                  background: reviewAction === "approve" ? "#16a34a" : "#dc2626",
                }}>
                {reviewAction === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expenses list */}
      <div style={{ display: "grid", gap: 10 }}>
        {(expenses as any[]).map((exp: any) => {
          const sc = STATUS_CONFIG[exp.status] || STATUS_CONFIG.pending;
          return (
            <div key={exp.id} className="expense-card-row" style={{
              background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
              padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 15, color: "#1e293b" }}>{exp.title}</span>
                  <span style={{
                    background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                    padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                  }}>{sc.label}</span>
                  <span style={{ background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>
                    {exp.category}
                  </span>
                  {exp.is_vat_claimable && (
                    <span style={{ background: "#f0fdf4", color: "#166534", border: "1px solid #86efac", padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>
                      VAT Claimable
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#64748b" }}>
                  <span style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>
                    {exp.currency_symbol}{(exp.amount || 0).toLocaleString("en", { minimumFractionDigits: 2 })}
                  </span>
                  {exp.wht_amount > 0 && (
                    <span>
                      WHT ({exp.wht_rate}%): -{exp.currency_symbol}{exp.wht_amount.toLocaleString("en", { minimumFractionDigits: 2 })}
                      <span style={{ fontWeight: 600, color: "#475569" }}> · Net: {exp.currency_symbol}{(exp.amount - exp.wht_amount).toLocaleString("en", { minimumFractionDigits: 2 })}</span>
                    </span>
                  )}
                  {isTeamView && <span style={{ fontWeight: 600, color: "#475569" }}>{exp.employee_name}</span>}
                  <span>{formatDate(exp.expense_date || exp.submitted_at, "short", tz)}</span>
                  {exp.description && <span style={{ color: "#94a3b8" }}>{exp.description}</span>}
                </div>
                {exp.reviewer_notes && (
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, fontStyle: "italic" }}>
                    Note: {exp.reviewer_notes}
                  </div>
                )}
              </div>
              <div className="expense-card-actions" style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                {exp.has_receipt && (
                  <AsyncButton onClick={() => handleDownloadReceipt(exp.id)} style={outlineBtnStyle}>Receipt</AsyncButton>
                )}
                {isTeamView && exp.status === "pending" && (
                  <>
                    <button onClick={() => { setReviewId(exp.id); setReviewAction("approve"); }} style={{
                      ...outlineBtnStyle, color: "#16a34a", borderColor: "#86efac", background: "#f0fdf4",
                    }}>Approve</button>
                    <button onClick={() => { setReviewId(exp.id); setReviewAction("reject"); }} style={{
                      ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5", background: "#fff5f5",
                    }}>Reject</button>
                  </>
                )}
                {isTeamView && exp.status === "approved" && (
                  <button onClick={() => paidMut.mutate(exp.id)} disabled={paidMut.isPending} style={{
                    ...outlineBtnStyle, color: "#1d4ed8", borderColor: "#93c5fd",
                  }}>Mark Paid</button>
                )}
                {exp.status === "pending" && (
                  <AsyncButton onClick={async () => { if (await confirm("Delete this claim?", { confirmLabel: "Delete" })) deleteMut.mutate(exp.id); }}
                    style={{ ...outlineBtnStyle, color: "#dc2626" }}>Delete</AsyncButton>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {(expenses as any[]).length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ display: "block", margin: "0 auto" }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/><line x1="9" y1="11" x2="15" y2="11"/>
          </svg>
          <div style={{ marginTop: 12 }}>No expense claims {statusFilter ? `with status "${statusFilter}"` : "yet"}.</div>
        </div>
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: "#1e3a8a",
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
